import React, { useRef } from "react";
import clsx from "clsx";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import ErrorMessage from "@/ui/atoms/ErrorMessage";
import AnchorUnderlined from "@/ui/atoms/AnchorUnderlined";

import {
    FileUpload as UIKitFileUpload,
    fileUploadTestIds as uiKitFileUploadTestIds,
} from "@proda-ai/ui-kit";
import Typography from "@/ui/atoms/Typography";

export type CoAUploadProps = Omit<BoxProps, "children"> & {
    error: string | null;
    handleFileDrop: (e: React.DragEvent<HTMLDivElement>) => void;
    handleFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
    InnerBoxProps?: React.ComponentProps<typeof UIKitFileUpload>;
};

export const testIds = {
    main: uiKitFileUploadTestIds.main,
    fileInput: "FileUpload-fileInput",
    fileInputButton: uiKitFileUploadTestIds.button,
    heading: uiKitFileUploadTestIds.heading,
};

const CoAUpload = (props: CoAUploadProps) => {
    const {
        sx,
        className,
        error,
        handleFileDrop,
        handleFileInput,
        InnerBoxProps,
        ...otherProps
    } = props;

    const { sx: innerSx, ...innerOtherProps } = InnerBoxProps || {};

    const fileInputRef = useRef<HTMLInputElement>(null);

    const heading = `Drag and drop Chart of Accounts Excel file to upload`;

    const handleButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <Box
            sx={{
                padding: "32px",
                ...sx,
            }}
            {...otherProps}
        >
            <UIKitFileUpload
                sx={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "32px",
                    boxSizing: "border-box",
                    ...innerSx,
                }}
                className={clsx([testIds.main, className])}
                buttonLabel="Choose file"
                caption="Only Excel files are supported"
                heading={heading}
                handleClick={handleButtonClick}
                onDrop={handleFileDrop}
                {...innerOtherProps}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileInput}
                    hidden
                    id="fileInput"
                    data-testid={testIds.fileInput}
                />

                {error !== null && error !== undefined ? (
                    <ErrorMessage title={error}>
                        <Typography variant="body2" sx={{ textAlign: "left" }}>
                            Please try again, and if the issue continues, please
                            contact your Customer Success Manager, or our{" "}
                            <AnchorUnderlined href="mailto:support@proda.ai">
                                Support Team
                            </AnchorUnderlined>{" "}
                            for help.
                        </Typography>
                    </ErrorMessage>
                ) : null}
            </UIKitFileUpload>
        </Box>
    );
};

export default CoAUpload;
